// src/pages/Dashboard/Dashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import DateRangeSelector from 'components/DateRangeSelector';
import { RefreshCw, Activity } from 'lucide-react';
import {
    CallQualificationDistributionBarGraph,
    CallTransferDistributionBarGraph,
    CallStatusDistributionBarGraph,
    SalesFunnelArea,
    AIvsHumanTimeBarGraph,
} from 'components/charts';
import { useGetDashboardData, DateRangeType } from 'services/hooks/queries';
import { InfoCards, TotalCallTime, TotalAITime } from 'components';
import { WorkflowOptions } from './components';
import styles from './Dashboard.module.scss';
import { Button, Select } from 'common/components';
import { CallDirection, CallDirectionType } from 'types/call.type';
import { DateRange } from './Dashboard.types';

const Dashboard: React.FC = () => {
    // Date range state
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [dateRangeType, setDateRangeType] = useState<keyof typeof DateRangeType>('today');

    const [selectedWorkflow, setSelectedWorkflow] = useState<string>('All Workflows');
    const [callDirection, setCallDirection] = useState<CallDirectionType>(CallDirection.ALL);

    // State for API data
    const [error, setError] = useState<Error | null>(null);

    const { dashboardData, isDashboardDataLoading, dashboardDataError, refetchDashboardData } = useGetDashboardData({
        startDate,
        endDate,
        selectedWorkflow,
        dateRangeType,
        callDirection,
    });

    // Helper function to calculate dates based on date range type
    const calculateDatesFromRangeType = useCallback(
        (type: keyof typeof DateRangeType): DateRange => {
            // Get current date at the beginning of the day in local time
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Create yesterday date at the beginning of the day
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let start = new Date(startDate);
            let end = new Date(endDate);

            switch (type) {
                case DateRangeType.today:
                    start = new Date(today);
                    end = new Date(today);
                    break;
                case DateRangeType.yesterday:
                    start = new Date(yesterday);
                    end = new Date(yesterday);
                    break;
                case DateRangeType.last7Days: {
                    // Get today as the end date (end of day)
                    end = new Date(today);

                    // Calculate start date as 7 days ago from today (beginning of day)
                    start = new Date(today);
                    start.setDate(start.getDate() - 6); // Subtract 6 days to include today (7 days total)
                    break;
                }
                case DateRangeType.last30Days: {
                    // Get today as the end date
                    end = new Date(today);

                    // Calculate start date as 30 days ago from today
                    start = new Date(today);
                    start.setDate(start.getDate() - 29); // Subtract 29 days to include today (30 days total)
                    break;
                }
                case DateRangeType.custom:
                    // For custom type, keep the current start and end dates
                    break;
                default:
                    break;
            }

            return { start, end };
        },
        [startDate, endDate]
    );

    useEffect(() => {
        if (dashboardDataError) {
            setError(dashboardDataError);
        }
    }, [dashboardDataError]);

    const isRefetchingActive = dateRangeType === DateRangeType.today;

    return (
        <div className={styles.dashboardContainer}>
            {/* Dashboard Header and Date Range Selector */}
            <div className={styles.dashboardFilters}>
                <div className={styles.dateRangeDisplay}>
                    <div className={styles.activityIconContainer}>
                        <Activity
                            size={16}
                            className={`${isRefetchingActive ? styles.animatePulse : styles.textGray}`}
                        />
                        <div className={styles.tooltip}>
                            Auto-refresh: Active for Today only (every 5 minutes). Historical dates use static data -
                            manual refresh available.
                        </div>
                    </div>

                    <h3>Dashboard Overview</h3>
                </div>

                <div className={styles.controlsWrapper}>
                    {/* Workflow Filter */}
                    <WorkflowOptions
                        selectedWorkflow={selectedWorkflow}
                        onWorkflowChange={setSelectedWorkflow}
                        dashboardData={dashboardData}
                        isDashboardDataLoading={isDashboardDataLoading}
                    />

                    <DateRangeSelector
                        initialDateRange={DateRangeType.today}
                        onChange={({
                            startDate: newStartDate,
                            endDate: newEndDate,
                            dateRangeType: newDateRangeType,
                        }: {
                            startDate: Date;
                            endDate: Date;
                            dateRangeType: keyof typeof DateRangeType;
                        }) => {
                            // Handle all date changes in one place to prevent multiple API calls
                            if (newDateRangeType !== dateRangeType) {
                                // When date range type changes, calculate the correct dates
                                const { start, end } = calculateDatesFromRangeType(newDateRangeType);

                                // Set state values
                                setDateRangeType(newDateRangeType);
                                setStartDate(newDateRangeType === DateRangeType.custom ? newStartDate : start);
                                setEndDate(newDateRangeType === DateRangeType.custom ? newEndDate : end);
                            } else if (newDateRangeType === DateRangeType.custom) {
                                // For custom dates, update directly
                                setStartDate(newStartDate);
                                setEndDate(newEndDate);
                            }
                        }}
                    />

                    {/* Call Direction Filter */}
                    <div style={{ minWidth: 180 }}>
                        <Select
                            value={callDirection}
                            onChange={(e) => setCallDirection(e.target.value as CallDirectionType)}
                            options={[
                                { value: CallDirection.ALL, label: 'All Calls' },
                                { value: CallDirection.OUTBOUND, label: 'Outbound' },
                                { value: CallDirection.INBOUND, label: 'Inbound' },
                            ]}
                        />
                    </div>

                    <div className={styles.mlAuto}>
                        <Button
                            variant='outline-secondary'
                            onClick={() => refetchDashboardData()}
                            icon={RefreshCw}
                            className={styles.ctaButton}
                            isIconSpinning={isDashboardDataLoading}
                        >
                            <span>Refresh Data</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Loading and Error States */}
            {isDashboardDataLoading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                </div>
            )}

            {error && <p className={styles.errorMessage}>{error.message || 'An error occurred'}</p>}

            {!isDashboardDataLoading && !error && dashboardData && (
                <>
                    {/* Info Cards Row */}
                    <div className={styles.infoCardsRow}>
                        {dashboardData.infos && <InfoCards infos={dashboardData.infos} />}
                        {dashboardData.charts?.callTimeDistributionBar && (
                            <TotalCallTime callData={dashboardData.charts.callTimeDistributionBar} />
                        )}
                        {dashboardData.charts?.callTimeDistributionBar && (
                            <TotalAITime callData={dashboardData.charts.callTimeDistributionBar} />
                        )}
                    </div>

                    {/* Top Charts Row */}
                    <div className={styles.chartsRow}>
                        {/* Call Status Distribution */}
                        {!!dashboardData.charts?.callStatusDistributionBar?.dataPoints?.length && (
                            <CallStatusDistributionBarGraph
                                data={dashboardData.charts.callStatusDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callStatusDistributionBar.totalCount}
                                height={150}
                                dateRangeType={dateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Status Overview'
                            />
                        )}

                        {/* Call Transfer Distribution */}
                        {!!dashboardData.charts?.callTransferDistributionBar?.dataPoints?.length && (
                            <CallTransferDistributionBarGraph
                                data={dashboardData.charts.callTransferDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callTransferDistributionBar.totalCount}
                                height={150}
                                dateRangeType={dateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Transfer Distribution'
                            />
                        )}
                    </div>

                    {/* Middle Charts Row */}
                    <div className={styles.chartsRow}>
                        {/* Call Qualification Distribution */}
                        {!!dashboardData.charts?.callQualificationDistributionBar?.dataPoints?.length && (
                            <CallQualificationDistributionBarGraph
                                data={dashboardData.charts.callQualificationDistributionBar.dataPoints}
                                amountofCalls={dashboardData.charts.callQualificationDistributionBar.totalCount}
                                height={150}
                                dateRangeType={dateRangeType}
                                startDate={startDate}
                                endDate={endDate}
                                title='Call Qualification Distribution'
                            />
                        )}

                        {/* AI vs Human Time Bar Graph */}
                        {!!dashboardData.charts?.callTimeDistributionBar?.totalCount && (
                            <AIvsHumanTimeBarGraph
                                data={dashboardData.charts.callTimeDistributionBar}
                                height={250}
                                title='AI vs Human Time'
                            />
                        )}
                    </div>

                    {/* Sales Funnel Row - Full Width */}
                    {!!dashboardData.charts?.salesFunnelArea?.totalCount && (
                        <div className={styles.salesFunnelRow}>
                            <div className={styles.chartCard}>
                                <SalesFunnelArea
                                    data={dashboardData.charts.salesFunnelArea}
                                    height={250}
                                    title='Sales Conversion Funnel'
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Dashboard;
